import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ReportHeader } from '../components/campaign-report/ReportHeader';
import { KpiGrid } from '../components/campaign-report/KpiGrid';
import { ReportTabs } from '../components/campaign-report/ReportTabs';
import { parseCampaignReportMarkdown, isMarkdownStructuredReport } from '../lib/campaignReportParser';
import { convertFormDataToCampaignReport, isFormDataStructured } from '../lib/campaignReportDataConverter';
import { CampaignReportData } from '../types/campaignReport';
import { ArrowLeft, Download, Calendar } from 'lucide-react';

type CampaignReport = {
  id: string;
  campaignName: string;
  customReportData?: string | null;
  updatedAt?: string;
  Brand?: {
    id: string;
    name: string;
  };
};

export default function BrandCampaignReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [reportData, setReportData] = useState<CampaignReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadReport();
    }
  }, [id]);

  async function loadReport() {
    if (!id) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/admin/custom-reports/${id}`, {
        credentials: 'include',
      });
      
      if (res.ok) {
        const data = await res.json();
        setReport(data);

        // Parse customReportData
        if (data.customReportData) {
          try {
            // Try parsing as JSON first
            let parsedData: CampaignReportData | null = null;
            let jsonData: any = null;

            if (typeof data.customReportData === 'string') {
              try {
                jsonData = JSON.parse(data.customReportData);
              } catch {
                // If JSON.parse fails, treat as markdown text
                const markdownData = parseCampaignReportMarkdown(data.customReportData);
                if (markdownData) {
                  parsedData = markdownData;
                }
              }
            } else {
              jsonData = data.customReportData;
            }

            // If we have JSON data, check if it's form data or markdown
            if (jsonData && !parsedData) {
              if (isFormDataStructured(jsonData)) {
                // Convert form data to campaign report structure
                parsedData = convertFormDataToCampaignReport(jsonData);
              } else if (jsonData.markdown || jsonData.text) {
                // Try parsing markdown from JSON
                const markdownText = jsonData.markdown || jsonData.text;
                parsedData = parseCampaignReportMarkdown(markdownText);
              } else {
                // Try parsing the entire JSON as markdown (might be stringified markdown)
                const stringified = JSON.stringify(jsonData);
                parsedData = parseCampaignReportMarkdown(stringified);
              }
            }

            if (parsedData) {
              setReportData(parsedData);
            } else {
              setError('Failed to parse report data. Please ensure the report contains valid data.');
            }
          } catch (e) {
            console.error('Error parsing report data:', e);
            setError('Failed to parse report data.');
          }
        } else {
          setError('No report data found.');
        }
      } else {
        setError('Failed to load report');
      }
    } catch (error) {
      console.error('Error loading report:', error);
      setError('Failed to load report');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="container mx-auto p-6 max-w-7xl">
          <div className="space-y-6">
            <Skeleton className="h-24 w-full" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !report || !reportData) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="container mx-auto p-6 max-w-7xl">
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground mb-4">{error || 'Report not found'}</p>
              <div className="flex justify-center gap-4">
                <Button onClick={() => navigate('/admin/custom-reports')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Custom Reports
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="outline" onClick={() => navigate('/admin/custom-reports')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-4">
            {report.updatedAt && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>Last updated: {new Date(report.updatedAt).toLocaleDateString()}</span>
              </div>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => alert('PDF export coming soon')}>
                  Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => alert('CSV export coming soon')}>
                  Export as CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Report Header */}
        <ReportHeader campaignName={report.campaignName} meta={reportData.meta} />

        {/* KPI Grid */}
        <KpiGrid kpis={reportData.kpis} />

        {/* Report Tabs */}
        <ReportTabs data={reportData} />
      </div>
    </div>
  );
}
